import * as React from "react";
import {
  Box,
  HStack,
  Icon,
  IconButton,
  IconButtonProps,
  chakra,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  useAuditCommentsSectionIndicator,
  useAuditCommentsSubsectionIndicator,
  useAuditCommentsSharedSectionIndicator,
  useAuditCommentsSharedSubsectionIndicator,
} from "modules/charts-shared/api/queries/useAuditCommentsIndicator";
import { AuditCommentsIndicator } from "modules/charts-shared/types/audit-comments";

import {
  EmrAuditComment,
  EmrAuditCommentAlt,
  EmrAuditCommentCheck,
  EmrCheckCircle,
} from "@medstonetech/slate-icons";
import { UseMutateFunction } from "react-query";
import {
  AuditCommentsModal,
  AuditCommentsSharedModal,
} from "./AuditCommentsModal";
import { useAuditPermissions, useChartTrackingContext } from "contexts";
import { useFormContext } from "react-hook-form";
import {
  useUpdateAuditCommentsCheck,
  useUpdateAuditCommentsSharedCheck,
} from "modules/charts-shared/api";
import { formMessages, genericErrors } from "messages";
import { extractApiErrorMessage } from "utils";

type AuditCommentsSectionProps = {
  chartCode: string;
  sectionCode: string;
  encounterId: string;
  isFromSideBar: boolean;
  isFromSubSection?: boolean;
  hasAuditsCheck?: boolean;
};

type AuditCommentsSharedSectionProps = {
  sectionCode: string;
  encounterId: string;
  isFromSideBar: boolean;
};

type AuditCommentsSubsectionProps = {
  chartCode: string;
  sectionCode: string;
  encounterId: string;
  subsectionCode: string;
  subsectionLabel: string;
  isFromSideBar: boolean;
};

type AuditCommentsSharedSubsectionProps = {
  chartCode: string;
  sectionCode: string;
  encounterId: string;
  subsectionCode: string;
  subsectionLabel: string;
  isFromSideBar: boolean;
};

type AuditCommentsIndicatorButtonProps = {
  data?: AuditCommentsIndicator;
  updateCheck?: UseMutateFunction<unknown, unknown, unknown>;
  isUpdateCheckLoading?: boolean;
  isFromSideBar: boolean;
  onClickCheckCallback?(): void;
  onClickCommentCallback?(): void;
  hasAuditsCheck?: boolean;
  hasAuditsReview?: boolean;
};

type FieldsetImmuneIconButtonProps = Omit<
  IconButtonProps,
  "onClick" | "onKeyDown"
> & {
  onClick?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
};

function FieldsetImmuneIconButton(props: FieldsetImmuneIconButtonProps) {
  const { isDisabled, isLoading, onClick, onKeyDown, ...rest } = props;

  const isDisabledOrLoading = Boolean(isDisabled || isLoading);

  const handleClick = React.useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(() => {
    if (isDisabledOrLoading) return;
    onClick?.();
  }, [isDisabledOrLoading, onClick]);

  const handleKeyDown = React.useCallback<
    React.KeyboardEventHandler<HTMLDivElement>
  >(
    (event) => {
      onKeyDown?.(event);
      if (isDisabledOrLoading) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick?.();
      }
    },
    [isDisabledOrLoading, onClick, onKeyDown]
  );

  return (
    <IconButton
      {...rest}
      as={Box}
      role="button"
      aria-disabled={isDisabledOrLoading}
      tabIndex={isDisabledOrLoading ? -1 : 0}
      pointerEvents="auto"
      cursor={isDisabledOrLoading ? "not-allowed" : "pointer"}
      isDisabled={false}
      isLoading={isLoading}
      onClick={handleClick}
      onKeyDown={
        handleKeyDown as unknown as React.KeyboardEventHandler<HTMLButtonElement>
      }
    />
  );
}

function AuditCommentsIndicatorButton(
  props: AuditCommentsIndicatorButtonProps
) {
  const {
    data,
    isFromSideBar,
    onClickCheckCallback,
    onClickCommentCallback,
    hasAuditsCheck = false,
    hasAuditsReview = false,
    isUpdateCheckLoading,
  } = props;

  const { isCommenter } = useAuditPermissions();
  const { isCurrentChartCompleted } = useChartTrackingContext();

  const isAuditCheckIndicatorAvailable = hasAuditsCheck;
  const isAuditReviewIndicatorAvailable = hasAuditsReview;
  const isAuditCommentsAvailable = data?.hasComments;
  const disableAuditInteractions = isCurrentChartCompleted && !isCommenter;

  return isFromSideBar ? (
    <Box display="flex" justifyContent="space-between" padding="5px">
      {isCommenter && isAuditCommentsAvailable ? (
        <IconButton
          aria-label="audit-comment"
          icon={
            <Icon
              as={
                isAuditReviewIndicatorAvailable
                  ? EmrAuditCommentCheck
                  : EmrAuditComment
              }
              color={isAuditReviewIndicatorAvailable ? "green" : "orange"}
              fontSize="1.55rem"
            />
          }
          variant="ghost"
          minWidth="unset"
          position="relative"
          height="2rem"
          top="0rem"
          width="1rem"
        />
      ) : (
        <IconButton
          aria-label="audit-check"
          icon={
            isAuditReviewIndicatorAvailable ? (
              <Icon
                as={EmrAuditCommentCheck}
                color={"green"}
                fontSize="1.55rem"
              />
            ) : isAuditCommentsAvailable ? (
              <Icon as={EmrAuditComment} color={"orange"} fontSize="1.55rem" />
            ) : (
              <Icon
                as={EmrCheckCircle}
                color={isAuditCheckIndicatorAvailable ? "green" : "gray.450"}
                fontSize="1.45rem"
              />
            )
          }
          variant="ghost"
          minWidth="unset"
          position="relative"
          height="2rem"
          top="0rem"
          width="1rem"
          isLoading={isUpdateCheckLoading}
          isDisabled={disableAuditInteractions}
        />
      )}
    </Box>
  ) : (
    <HStack gap=".25rem">
      {isCommenter ? (
        <Box display="flex" justifyContent="space-between" padding="5px">
          <FieldsetImmuneIconButton
            aria-label="notes"
            icon={
              isAuditReviewIndicatorAvailable ? (
                <Icon
                  as={EmrAuditCommentCheck}
                  color={"green"}
                  fontSize="1.75rem"
                />
              ) : (
                <Icon
                  as={
                    isAuditCommentsAvailable
                      ? EmrAuditComment
                      : EmrAuditCommentAlt
                  }
                  color={isAuditCommentsAvailable ? "orange" : "gray.450"}
                  fontSize="1.75rem"
                />
              )
            }
            variant="ghost"
            minWidth="unset"
            position="relative"
            height="2rem"
            top="0.1rem"
            width="2rem"
            isDisabled={disableAuditInteractions}
            onClick={() =>
              !disableAuditInteractions &&
              onClickCommentCallback &&
              onClickCommentCallback()
            }
          />
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" padding="5px">
            <FieldsetImmuneIconButton
              aria-label="audit-check"
              icon={
                <Icon
                  as={EmrCheckCircle}
                  color={isAuditCheckIndicatorAvailable ? "green" : "gray.450"}
                  fontSize="1.65rem"
                />
              }
              variant="ghost"
              minWidth="unset"
              position="relative"
              height="2rem"
              top="0.05rem"
              width="2rem"
              isLoading={isUpdateCheckLoading}
              isDisabled={isAuditCommentsAvailable || disableAuditInteractions}
              onClick={() => {
                if (!disableAuditInteractions && onClickCheckCallback) {
                  onClickCheckCallback();
                }
              }}
            />
          </Box>

          <Box display="flex" justifyContent="space-between" padding="5px">
            <FieldsetImmuneIconButton
              aria-label="audit-comment"
              icon={
                <Icon
                  as={
                    isAuditReviewIndicatorAvailable
                      ? EmrAuditCommentCheck
                      : isAuditCommentsAvailable
                      ? EmrAuditComment
                      : EmrAuditCommentAlt
                  }
                  color={
                    isAuditReviewIndicatorAvailable
                      ? "green"
                      : isAuditCommentsAvailable
                      ? "orange"
                      : "gray.450"
                  }
                  fontSize="1.75rem"
                />
              }
              variant="ghost"
              minWidth="unset"
              position="relative"
              height="2rem"
              top="0.1rem"
              width="2rem"
              isDisabled={disableAuditInteractions}
              onClick={() =>
                !disableAuditInteractions &&
                onClickCommentCallback &&
                onClickCommentCallback()
              }
            />
          </Box>
        </>
      )}
    </HStack>
  );
}

function AuditCommentsSectionIndicator(props: AuditCommentsSectionProps) {
  const {
    encounterId,
    sectionCode,
    chartCode,
    isFromSideBar,
    isFromSubSection = false,
  } = props;

  const { data } = useAuditCommentsSectionIndicator({
    chartCode,
    encounterId,
    sectionCode,
    validateSubsections: isFromSideBar || isFromSubSection,
  });

  return (
    <Box>
      <AuditCommentsIndicatorButton
        data={data?.data}
        isFromSideBar={isFromSideBar}
        hasAuditsCheck={data?.data.hasCheck}
        hasAuditsReview={data?.data.hasReview}
      />
    </Box>
  );
}

function AuditCommentsSubsectionIndicator(props: AuditCommentsSubsectionProps) {
  const {
    encounterId,
    sectionCode,
    subsectionCode,
    subsectionLabel,
    chartCode,
    isFromSideBar,
  } = props;

  const { watch, setValue, getValues } = useFormContext();

  const subsection = watch(subsectionCode);
  const subsections = watch("subsections");
  const hasAuditsCheck = React.useMemo(() => {
    if (sectionCode === "SDIS") {
      return subsections[subsectionCode]?.auditCheck as boolean;
    }
    return subsection?.auditCheck as boolean;
  }, [sectionCode, subsection?.auditCheck, subsectionCode, subsections]);

  const { isOpen, onClose, onOpen } = useDisclosure();

  const { data, isLoading } = useAuditCommentsSubsectionIndicator({
    chartCode,
    encounterId,
    sectionCode,
    subsectionCode,
  });

  const setAuditCheck = React.useCallback(
    (value: boolean) => {
      const path =
        sectionCode === "SDIS"
          ? `subsections.${subsectionCode}`
          : subsectionCode;
      const currentValue = getValues(path) as
        | { auditCheck?: boolean }
        | undefined;
      setValue(
        path,
        { ...currentValue, auditCheck: value },
        { shouldDirty: true, shouldTouch: true }
      );
    },
    [getValues, sectionCode, setValue, subsectionCode]
  );

  const onCheck = () => {
    setAuditCheck(!hasAuditsCheck);
  };

  const handleCommentCreated = React.useCallback(() => {
    const auditCheck = getValues(
      sectionCode === "SDIS"
        ? `subsections.${subsectionCode}.auditCheck`
        : `${subsectionCode}.auditCheck`
    ) as boolean | undefined;
    // avoid marking the form dirty when already unchecked
    if (auditCheck) {
      setAuditCheck(false);
    }
  }, [getValues, sectionCode, setAuditCheck, subsectionCode]);

  return (
    <Box>
      <AuditCommentsIndicatorButton
        data={data?.data}
        isFromSideBar={isFromSideBar}
        onClickCheckCallback={onCheck}
        onClickCommentCallback={onOpen}
        hasAuditsCheck={hasAuditsCheck}
        hasAuditsReview={data?.data.hasReview}
      />

      {isOpen && (
        <AuditCommentsModal
          isOpen={isOpen}
          onClose={onClose}
          chartCode={chartCode}
          encounterId={encounterId}
          sectionCode={sectionCode}
          subsectionCode={subsectionCode}
          commentsIndicator={data?.data}
          commentsIndicatorLoading={isLoading}
          onCommentCreated={handleCommentCreated}
          title={
            <chakra.span fontSize="1.0625rem" fontWeight="600" color="black">
              Subsection: {subsectionLabel}
            </chakra.span>
          }
        />
      )}
    </Box>
  );
}

function AuditCommentsSharedSectionIndicator(
  props: AuditCommentsSharedSectionProps
) {
  const { encounterId, sectionCode, isFromSideBar } = props;

  const { data } = useAuditCommentsSharedSectionIndicator({
    encounterId,
    sectionCode,
    validateSubsections: isFromSideBar,
  });

  return (
    <Box>
      <AuditCommentsIndicatorButton
        data={data?.data}
        isFromSideBar={isFromSideBar}
        hasAuditsCheck={data?.data.hasCheck}
        hasAuditsReview={data?.data.hasReview}
      />
    </Box>
  );
}

function AuditCommentsSharedSubsectionIndicator(
  props: AuditCommentsSharedSubsectionProps
) {
  const {
    chartCode,
    encounterId,
    sectionCode,
    subsectionCode,
    subsectionLabel,
    isFromSideBar,
  } = props;

  const { watch, getValues, setValue } = useFormContext();
  const hasAuditsCheck = watch(subsectionCode)?.auditCheck as boolean;

  const { isOpen, onClose, onOpen } = useDisclosure();

  const { data, isLoading } = useAuditCommentsSharedSubsectionIndicator({
    encounterId,
    sectionCode,
    subsectionCode,
  });

  const setAuditCheck = React.useCallback(
    (value: boolean) => {
      const currentValue = getValues(subsectionCode) as
        | { auditCheck?: boolean }
        | undefined;
      setValue(
        subsectionCode,
        { ...currentValue, auditCheck: value },
        { shouldDirty: true, shouldTouch: true }
      );
    },
    [getValues, setValue, subsectionCode]
  );

  const onCheck = () => {
    setAuditCheck(!hasAuditsCheck);
  };

  const handleCommentCreated = React.useCallback(() => {
    const auditCheck = getValues(`${subsectionCode}.auditCheck`) as
      | boolean
      | undefined;
    // avoid marking the form dirty when already unchecked
    if (auditCheck) {
      setAuditCheck(false);
    }
  }, [getValues, setAuditCheck, subsectionCode]);

  return (
    <Box>
      <AuditCommentsIndicatorButton
        data={data?.data}
        isFromSideBar={isFromSideBar}
        onClickCheckCallback={onCheck}
        onClickCommentCallback={onOpen}
        hasAuditsCheck={hasAuditsCheck}
        hasAuditsReview={data?.data.hasReview}
      />

      {isOpen && (
        <AuditCommentsSharedModal
          isOpen={isOpen}
          onClose={onClose}
          chartCode={chartCode}
          encounterId={encounterId}
          sectionCode={sectionCode}
          subsectionCode={subsectionCode}
          commentsIndicator={data?.data}
          commentsIndicatorLoading={isLoading}
          onCommentCreated={handleCommentCreated}
          title={
            <chakra.span fontSize="1.0625rem" fontWeight="600" color="black">
              Subsection: {subsectionLabel}
            </chakra.span>
          }
        />
      )}
    </Box>
  );
}

function AuditCommentsHeaderSectionIndicator(
  props: AuditCommentsSectionProps & { sectionLabel: string }
) {
  const toast = useToast();

  const { encounterId, sectionCode, chartCode, sectionLabel, isFromSideBar } =
    props;

  const { isOpen, onClose, onOpen } = useDisclosure();

  const requestParams = {
    encounterId,
    sectionCode,
    chartCode,
  };

  const { data, isLoading, isFetching } = useAuditCommentsSectionIndicator({
    chartCode,
    encounterId,
    sectionCode,
  });

  const { mutateAsync: updateCheck, isLoading: isCheckLoading } =
    useUpdateAuditCommentsCheck(requestParams);

  const onCheck = async () => {
    try {
      await updateCheck(null);
      toast({
        description: formMessages.updateSuccess("Audit section status"),
      });
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  return (
    <Box>
      <AuditCommentsIndicatorButton
        data={data?.data}
        isFromSideBar={isFromSideBar}
        onClickCheckCallback={onCheck}
        onClickCommentCallback={onOpen}
        hasAuditsCheck={data?.data.hasCheck}
        hasAuditsReview={data?.data.hasReview}
        isUpdateCheckLoading={isCheckLoading || isLoading || isFetching}
      />

      {isOpen && (
        <AuditCommentsModal
          isOpen={isOpen}
          onClose={onClose}
          chartCode={chartCode}
          encounterId={encounterId}
          sectionCode={sectionCode}
          commentsIndicator={data?.data}
          commentsIndicatorLoading={isLoading}
          title={
            <chakra.span fontSize="1.0625rem" fontWeight="600" color="black">
              Section: {sectionLabel}
            </chakra.span>
          }
        />
      )}
    </Box>
  );
}

function AuditCommentsHeaderSharedSectionIndicator(
  props: AuditCommentsSectionProps & { sectionLabel: string }
) {
  const toast = useToast();

  const {
    encounterId,
    sectionCode,
    chartCode,
    sectionLabel,
    isFromSideBar,
    isFromSubSection = false,
  } = props;

  const { isOpen, onClose, onOpen } = useDisclosure();

  const requestParams = {
    chartCode,
    encounterId,
    sectionCode,
    validateSubsections: isFromSideBar || isFromSubSection,
  };

  const { data, isLoading, isFetching } =
    useAuditCommentsSharedSectionIndicator(requestParams);

  const { mutateAsync: updateCheck, isLoading: isCheckLoading } =
    useUpdateAuditCommentsSharedCheck(requestParams);

  const onCheck = async () => {
    try {
      await updateCheck(null);
      toast({
        description: formMessages.updateSuccess("Audit section status"),
      });
    } catch (error) {
      toast({
        description:
          extractApiErrorMessage(error) || genericErrors.unknownError,
      });
    }
  };

  return (
    <Box>
      <AuditCommentsIndicatorButton
        data={data?.data}
        isFromSideBar={isFromSideBar}
        onClickCheckCallback={onCheck}
        onClickCommentCallback={onOpen}
        hasAuditsCheck={data?.data.hasCheck}
        hasAuditsReview={data?.data.hasReview}
        isUpdateCheckLoading={isCheckLoading || isLoading || isFetching}
      />

      {isOpen && (
        <AuditCommentsSharedModal
          isOpen={isOpen}
          onClose={onClose}
          chartCode={chartCode}
          encounterId={encounterId}
          sectionCode={sectionCode}
          commentsIndicator={data?.data}
          commentsIndicatorLoading={isLoading}
          title={
            <chakra.span fontSize="1.0625rem" fontWeight="600" color="black">
              Section: {sectionLabel}
            </chakra.span>
          }
        />
      )}
    </Box>
  );
}

export {
  AuditCommentsSectionIndicator,
  AuditCommentsSubsectionIndicator,
  AuditCommentsSharedSectionIndicator,
  AuditCommentsSharedSubsectionIndicator,
  AuditCommentsHeaderSectionIndicator,
  AuditCommentsHeaderSharedSectionIndicator,
};
